import { EmptyDataMessage } from '@/common/components/EmptyDataMessage';
import { CompanyBranchFinancialSummaryData } from '@/common/types/client/companyBranch.type';
import { formatCurrency, formatDecimalUnit, formatDecimalWithGroupedUnit } from '@/common/utils/formatters';
import { CornerDownRightIcon } from 'lucide-react';
import { Fragment } from 'react/jsx-runtime';
import { financialSummaryLabel, financialSummaryLabelPrivate } from '../../constants/financialSummaryLabel.constant';
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type';

interface FinancialSummaryProps {
  branchId: string;
  financialSummary?: CompanyBranchFinancialSummaryData;
  companyKind?: string;
  consumptionControlledBy?: CompanyContractConsumptionControlledBy
}

const FinancialSummary = ({ branchId, financialSummary, companyKind, consumptionControlledBy }: FinancialSummaryProps) => {
  const filteredSummariesKeys = financialSummary
    ? Object.keys(financialSummary).filter(summaryKey => financialSummary[summaryKey as keyof typeof financialSummary] && financialSummary[summaryKey as keyof typeof financialSummary].length)
    : [];

  if (!financialSummary || Number(filteredSummariesKeys?.length) === 0) {
    return <EmptyDataMessage className='overflow-hidden m-auto' imageHeight={100} />
  }

  const currentFinancialLabel =
    companyKind === 'private_company'
      ? financialSummaryLabelPrivate
      : financialSummaryLabel

  return filteredSummariesKeys.map((summaryKey, index) => (
    <Fragment key={`${branchId}_product_category_${index}`}>
      {index > 0 && <div className='h-px p-px bg-white-100'></div>}

      <div className='flex flex-col gap-1'>
        <p className='text-xs font-bold'>
          {currentFinancialLabel[summaryKey as keyof typeof currentFinancialLabel]}
        </p>

        <div className='flex flex-col gap-2'>
          {financialSummary[summaryKey as keyof typeof financialSummary].map((summaryItem, summaryItemIndex) => {
            const grantedSumValue = consumptionControlledBy === 'currency'
              ? formatCurrency(Number(summaryItem.granted_credit_sum))
              : formatDecimalWithGroupedUnit(
                summaryItem.granted_quantity_sum ?? 0,
                summaryItem.products,
                (item) => item.product_unit_for_quantity,
              );

            const remainingSumValue = consumptionControlledBy === 'currency'
              ? formatCurrency(Number(summaryItem.remaining_total_sum))
              : formatDecimalWithGroupedUnit(
                summaryItem.remaining_quantity_sum ?? 0,
                summaryItem.products,
                (item) => item.product_unit_for_quantity,
              );

            return (
              <Fragment key={`${branchId}_summary_${summaryItemIndex}`}>
                {summaryItemIndex > 0 && <div className='h-px p-px bg-white-100 ms-4'></div>}

                <div className='flex flex-col gap-1'>
                  <p className='text-xs font-medium'>
                    {summaryItem.category_name}
                  </p>

                  <div>
                    {summaryItem.total_value_sum && (
                      <p className='text-xs font-light italic text-yellow-400'>
                        Liberado: <span className='font-medium not-italic'>
                          {formatCurrency(Number(summaryItem.total_value_sum))}
                        </span>
                      </p>
                    )}

                    {(summaryItem.granted_credit_sum || summaryItem.granted_quantity_sum) && (
                      <p className='text-xs font-light italic text-yellow-400'>
                        Garantido: <span className='font-medium not-italic'>
                          {grantedSumValue}
                        </span>
                      </p>
                    )}

                    {(summaryItem.remaining_total_sum || summaryItem.remaining_quantity_sum) && (
                      <p className='text-xs font-light italic text-valid'>
                        Restante: <span className='font-medium not-italic'>
                          {remainingSumValue}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className='flex flex-col gap-2'>
                    {summaryItem.products?.map((product, index) => {
                      const productGrantedSumValue = consumptionControlledBy === 'currency'
                        ? formatCurrency(Number(product.granted_credit_sum))
                        : formatDecimalUnit(product.granted_quantity_sum ?? 0, product.product_unit_for_quantity)

                      const productRemainingSumValue = consumptionControlledBy === 'currency'
                        ? formatCurrency(Number(product.remaining_total_sum))
                        : formatDecimalUnit(product.remaining_quantity_sum ?? 0, product.product_unit_for_quantity)

                      const productTotalSumValue = consumptionControlledBy === 'currency'
                        ? formatCurrency(Number(product.total_value_sum))
                        : formatDecimalUnit(product.total_quantity_sum ?? 0, product.product_unit_for_quantity)

                      return (
                        <div key={`${branchId}_product_category_${index}_product_${index}`}>
                          <div className='flex items-center gap-1'>
                            <CornerDownRightIcon strokeWidth={1.5} className='size-3 text-white-400' />

                            <p className='text-[13px]'>
                              {product.product_name}
                            </p>
                          </div>

                          <div className='ps-4'>
                            {(product.total_value_sum || product.total_quantity_sum) && (
                              <p className='text-xs font-light italic text-yellow-400'>
                                Liberado: <span className='font-medium not-italic'>
                                  {productTotalSumValue}
                                </span>
                              </p>
                            )}

                            {(product.granted_credit_sum || product.granted_quantity_sum) && (
                              <p className='text-xs font-light italic text-yellow-400'>
                                Garantido: <span className='font-medium not-italic'>
                                  {productGrantedSumValue}
                                </span>
                              </p>
                            )}

                            {(product.remaining_total_sum || product.remaining_quantity_sum) && (
                              <p className='text-xs font-light italic text-valid'>
                                Restante: <span className='font-medium not-italic'>
                                  {productRemainingSumValue}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </Fragment>
            )
          })}
        </div>
      </div>
    </Fragment>
  ))
}

export default FinancialSummary;
